import React, { useState, useEffect, useRef } from "react";
import { Search, Package, MapPin, ArrowRight, X, Loader2 } from "lucide-react";
import API from "../services/api";
import { errMsg, prettyDate } from "./runsheets/util";

// ---------------------------------------------------------------------------
// Shared "why did this row match" helpers. The Orders board, the Harvey Norman
// tab and this panel all mark the words the operator typed the same way, so a
// row never leaves them guessing which product brought it up.
// ---------------------------------------------------------------------------
export const MARK_CLS = "bg-amber-400/30 text-amber-100 rounded px-0.5";

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The words of a query, longest first: when "pro" and "province" were both
// typed the regex alternation must try "province" first, or the mark would
// cover only "pro" and leave "vince" plain.
export const queryWords = (q) =>
  String(q || "").trim().split(/\s+/).filter(Boolean).sort((a, b) => b.length - a.length);

// Same rule as the API: every word must appear somewhere in the text.
export const matchesAll = (text, q) => {
  const words = queryWords(q);
  if (!words.length) return true;
  const hay = String(text == null ? "" : text).toLowerCase();
  return words.every((w) => hay.includes(w.toLowerCase()));
};

export const matchesAny = (text, q) => {
  const words = queryWords(q);
  if (!words.length) return false;
  const hay = String(text == null ? "" : text).toLowerCase();
  return words.some((w) => hay.includes(w.toLowerCase()));
};

export function Highlight({ text, query, className }) {
  const s = text == null ? "" : String(text);
  const words = queryWords(query);
  if (!s || !words.length) return <>{s}</>;
  // one capturing group: split() keeps the matched pieces at the odd indexes
  const re = new RegExp(`(${words.map(escapeRe).join("|")})`, "ig");
  return (
    <>
      {s.split(re).map((part, i) => (
        i % 2 === 1
          ? <mark key={i} className={className || MARK_CLS}>{part}</mark>
          : <React.Fragment key={i}>{part}</React.Fragment>
      ))}
    </>
  );
}

// Status chip. The picked_up / delivered flags are what the driver actually
// ticked, so they win over the free-text status; a portal row is only a plan.
const STATUS_CLS = {
  delivered: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  picked_up: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  planned: "text-indigo-300 border-indigo-500/30 bg-indigo-500/10",
  pending: "text-slate-300 border-slate-600/50 bg-slate-700/30",
};
const statusOf = (hit) => {
  if (hit.source === "hn_portal") return "planned";
  if (hit.delivered) return "delivered";
  if (hit.picked_up) return "picked_up";
  return hit.status || "pending";
};
const StatusChip = ({ hit }) => {
  const s = statusOf(hit);
  const cls = STATUS_CLS[s] || STATUS_CLS.pending;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full border text-[8px] font-semibold uppercase tracking-wide whitespace-nowrap ${cls}`}>
      {String(s).replace(/_/g, " ")}
    </span>
  );
};

// Live product search: "which truck is the PROVINCE buffet on, and where is
// it going?" without opening orders one by one. Queries
// GET /jobs/product-search as the operator types; the orders board defaults
// to today, so this looks back 90 days (or 400 with the toggle) and always
// includes tomorrow's Harvey Norman plan.
export default function ProductFinder({ onSelectOrder, onClose }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [allDates, setAllDates] = useState(false);
  const [hits, setHits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(-1);        // keyboard cursor over the rows
  const reqSeq = useRef(0);                          // drops answers that a later keystroke made stale
  const rowRefs = useRef([]);
  const days = allDates ? 400 : 90;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (debounced.length < 2) {
      setHits([]); setTotal(0); setError(null); setLoading(false);
      return;
    }
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    API.get(`/jobs/product-search?q=${encodeURIComponent(debounced)}&days=${days}&limit=200`)
      .then((res) => {
        if (seq !== reqSeq.current) return;
        setHits(Array.isArray(res.data?.hits) ? res.data.hits : []);
        setTotal(Number(res.data?.total) || 0);
        setActive(-1);
      })
      .catch((err) => {
        if (seq !== reqSeq.current) return;
        setHits([]); setTotal(0); setError(errMsg(err));
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [debounced, days]);

  // Escape closes from anywhere in the panel, not only while the input has focus.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const el = rowRefs.current[active];
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [active]);

  // A portal row has no order behind it yet (it becomes one when tomorrow's
  // run is built), so only an orders hit opens the detail view. OrderDetail
  // fetches /jobs/{id} itself; invoiceNo is its pre-fetch fallback label.
  const open = (hit) => {
    if (!hit || hit.source !== "orders" || !hit.order_id || !onSelectOrder) return;
    onSelectOrder({
      id: hit.order_id,
      order_number: hit.order_number,
      invoice_number: hit.invoice_number,
      invoiceNo: hit.invoice_number,
    });
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Enter" && active >= 0) {
      open(hits[active]);
    }
  };

  const ready = debounced.length >= 2;
  const countText = !ready || loading ? ""
    : total > hits.length ? `${total} matches · showing the first ${hits.length}`
    : `${total} match${total === 1 ? "" : "es"}`;
  const windowText = allDates ? "the last 400 days" : "the last 90 days";

  let body;
  if (!ready) {
    body = (
      <div className="px-6 py-12 text-center">
        <Package size={28} className="mx-auto mb-3 text-slate-600" />
        <p className="text-slate-400 text-sm font-medium">Type at least 2 characters</p>
        <p className="text-slate-600 text-xs mt-1">A SKU like PVEB02, or words from the description like “province buf” — every word has to appear.</p>
      </div>
    );
  } else if (error) {
    body = <div className="px-6 py-10 text-center text-sm text-red-300">{error}</div>;
  } else if (loading && hits.length === 0) {
    body = (
      <div className="px-4 py-3 space-y-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton h-6 w-full" style={{ opacity: 1 - i * 0.18 }}></div>
        ))}
      </div>
    );
  } else if (hits.length === 0) {
    body = (
      <div className="px-6 py-12 text-center">
        <Search size={26} className="mx-auto mb-3 text-slate-600" />
        <p className="text-slate-400 text-sm font-medium">
          No product matching “{debounced}” in {windowText} or tomorrow's Harvey Norman run
        </p>
        {/* Store paperwork abbreviates: a mattress is "MAT", a buffet "BUF",
            a tallboy "TB" - the whole word finds nothing. */}
        <p className="text-slate-600 text-xs mt-1">
          Paperwork abbreviates names: try a shorter part of the word (“mat” for mattress, “buf” for buffet) or the SKU.
        </p>
        {!allDates && (
          <p className="text-slate-600 text-xs mt-1">
            Older delivery?{" "}
            <button type="button" onClick={() => setAllDates(true)} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
              Search the last 400 days
            </button>
          </p>
        )}
      </div>
    );
  } else {
    body = (
      <table className="w-full text-sm text-slate-300">
        <thead className="sticky top-0 z-10 bg-[#0d1526]/95 backdrop-blur text-slate-500 border-b border-slate-800 text-[10px] uppercase tracking-[0.12em] font-semibold">
          <tr>
            <th className="px-4 py-2.5 text-left">Product</th>
            <th className="px-4 py-2.5 text-left">From</th>
            <th className="px-4 py-2.5 text-left">To</th>
            <th className="px-4 py-2.5 text-left">Date</th>
            <th className="px-4 py-2.5 text-left">Order</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60">
          {hits.map((hit, i) => {
            const isOrder = hit.source === "orders" && !!hit.order_id;
            const p = hit.product || {};
            const line = p.line || [p.sku, p.description].filter(Boolean).join(" ");
            const to = hit.to || "";
            const cust = hit.customer || "";
            // hn_portal rows already lead "to" with the customer; don't say it twice
            const showCust = !!cust && !to.toLowerCase().startsWith(cust.toLowerCase());
            const qty = p.quantity == null || p.quantity === "" ? 1 : p.quantity;
            return (
              <tr
                key={`${hit.source}-${hit.order_id || hit.job_no || ""}-${i}`}
                ref={(el) => { rowRefs.current[i] = el; }}
                tabIndex={isOrder ? 0 : -1}
                onClick={() => open(hit)}
                onKeyDown={(e) => { if (e.key === "Enter") open(hit); }}
                onMouseEnter={() => setActive(i)}
                title={isOrder ? "Open this order" : "Planned for tomorrow's Harvey Norman run — see Run Sheets › Harvey Norman Imports"}
                className={`align-top outline-none ${isOrder ? "cursor-pointer hover:bg-blue-500/[0.06]" : "cursor-default"} ${active === i ? "bg-blue-500/[0.08]" : ""}`}
              >
                <td className="px-4 py-2.5 min-w-[220px]">
                  <div className="text-slate-200 leading-snug"><Highlight text={line} query={debounced} /></div>
                  <div className="text-[11px] text-slate-500 tnum">
                    {p.sku && !line.includes(p.sku) ? `${p.sku} · ` : ""}×{qty}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-emerald-300/90 whitespace-nowrap max-w-[170px] truncate" title={hit.from || undefined}>
                  {hit.from || <span className="text-slate-600">—</span>}
                </td>
                <td className="px-4 py-2.5 text-xs max-w-[280px]">
                  {showCust && <div className="text-slate-200 font-medium truncate" title={cust}>{cust}</div>}
                  <div className="text-slate-400 flex items-start gap-1" title={to || undefined}>
                    <MapPin size={11} className="mt-0.5 shrink-0 text-slate-500" />
                    <span className="line-clamp-2">{to || "—"}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                  {hit.date ? prettyDate(hit.date) : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-indigo-400">{hit.invoice_number || hit.order_number || "—"}</span>
                    <StatusChip hit={hit} />
                    {isOrder && <ArrowRight size={12} className="text-slate-600" />}
                  </div>
                  {hit.source === "hn_portal" && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-semibold uppercase tracking-wide">
                        HN portal · tomorrow
                      </span>
                      {hit.job_no && <span className="font-mono text-[10px] text-slate-400">{hit.job_no}</span>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Find a product"
      className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh]"
    >
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-800">
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl p-2 shrink-0">
          <Package size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white">Find a product</h3>
          <p className="text-xs text-slate-500">
            Where is it going? Searches every product line on the orders board and tomorrow's Harvey Norman plan as you type.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800" title="Close (Esc)">
          <X size={18} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-800 bg-slate-950/40">
        <div className="flex items-center bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 flex-1 min-w-[240px] focus-within:border-blue-500 transition-all">
          <Search size={16} className="text-slate-400 mr-2 shrink-0" />
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Type a product name or SKU…"
            className="outline-none bg-transparent text-sm w-full text-slate-200 placeholder:text-slate-500"
          />
          {loading ? (
            <Loader2 size={15} className="animate-spin text-blue-400 shrink-0" />
          ) : q ? (
            <button type="button" onClick={() => setQ("")} className="text-slate-500 hover:text-white shrink-0" title="Clear">
              <X size={14} />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setAllDates((v) => !v)}
          aria-pressed={allDates}
          title={allDates ? "Back to the last 90 days" : "Look through every order, not just the last 90 days"}
          className={`border rounded-xl px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap ${
            allDates ? "bg-slate-700 text-white border-slate-600" : "text-slate-400 hover:text-white border-slate-700"}`}
        >
          {allDates ? "Searching the last 400 days" : "Search the last 400 days"}
        </button>
        {countText && <span className="text-xs text-slate-500 tnum whitespace-nowrap">{countText}</span>}
      </div>

      <div className="overflow-auto flex-1">
        {body}
      </div>

      <div className="px-5 py-2 border-t border-slate-800 text-[10px] text-slate-600">
        ↑ ↓ move · Enter opens an order · Esc closes
      </div>
    </div>
  );
}
